import React from 'react';
import {hydrate, render} from 'react-dom';
import {Provider} from 'react-redux';
import {loadableReady} from '@loadable/component';
import AbacusClientUtils from '@homeaway/abacus-client-utils';
import {configureStore} from '../../shared/startup/configureStore';
import {withErrorBoundary} from '../../shared/components/common/error-boundary/ErrorBoundary';
import LazyLoad from '../../shared/components/common/lazy-load/LazyLoad';
import {LAZY_LOAD_OFFSET} from '../../shared/utils/constants';
import {SearchFormContainer} from '../../shared/components/common/search-form/SearchForm';
import {TESTS} from '../../shared/utils/abacus';
import client from './client';
import {ApolloProvider} from 'react-apollo';
import {LocalizationContextProvider} from '@homeaway/react-localization-components';
import {register} from '../../shared/utils/mab-client';
import * as TCS from '../../shared/components/common/hero-image/options';

const {DEFAULT: LAZY_LOAD_OFFSET_DEFAULT} = LAZY_LOAD_OFFSET;

if (
    process.env.NODE_ENV === 'development' &&
    process.env.LANDINGAPP_DEBUG_REACT === '1'
) {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');
    whyDidYouRender(React, {
        trackAllPureComponents: true,
        logOwnerReasons: true,
        collapseGroups: true,
    });
}

function hydrateContent(store, Template) {
    const {
        config: {
            url: {prefix},
        },
        siteContext,
    } = store.getState();

    return loadableReady().then(() => {
        hydrate(
            <ApolloProvider client={client({prefix, siteContext})}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        <Template />
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.content-root')
        );
    });
}

/* eslint consistent-return:0 */
export default function hydrateApp(Template) {
    // Grab the state from a global injected into server-generated HTML
    const preloadedState = window.__PRELOADED_STATE__;

    // Create Redux store with initial state
    const store = configureStore({
        initialState: preloadedState,
    });

    const {
        abacus,
        config: {
            url: {prefix},
        },
        options,
        siteContext,
        mab,
    } = store.getState();

    window.__SITE_CONTEXT__ = siteContext;

    register(window.edap, mab);

    AbacusClientUtils.initialize(abacus);

    if (abacus[TESTS.POLYFILL_SERVICE]) {
        AbacusClientUtils.logExperiment(abacus[TESTS.POLYFILL_SERVICE]);
    }

    if (options.tcsMode !== TCS.UNAVAILABLE) {
        AbacusClientUtils.logExperiment(abacus[TESTS.USE_TCS_HERO]);
    }

    hydrate(
        <Provider store={store}>
            {withErrorBoundary(<SearchFormContainer />)}
        </Provider>,
        document.querySelector('.searchbox-root')
    );

    if (options.breadcrumbLocation === 'header') {
        import(
            /* webpackChunkName: "breadcrumbs" */
            '../../shared/modules/BreadCrumbs'
        ).then((module) => {
            const Breadcrumbs = module.default;
            hydrate(
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        {withErrorBoundary(<Breadcrumbs isInHeader />)}
                    </LocalizationContextProvider>
                </Provider>,
                document.querySelector('.breadcrumbs-root')
            );
        });
    }

    import(
        /* webpackChunkName: "site-header" */
        /* webpackPreload: true */
        '../../shared/components/common/site-header/SiteHeader'
    ).then((module) => {
        const SiteHeader = module.default;
        render(
            <ApolloProvider client={client({prefix, siteContext})}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        {withErrorBoundary(<SiteHeader />)}
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.site-header-root')
        );
    });

    import(
        /* webpackChunkName: "hero-image" */
        /* webpackPreload: true */
        '../../shared/components/common/hero-image/index'
    ).then((module) => {
        const HeroImageWrapper = module.default;
        hydrate(
            <ApolloProvider client={client({prefix, siteContext})}>
                <Provider store={store}>
                    <LocalizationContextProvider siteContext={siteContext}>
                        {withErrorBoundary(<HeroImageWrapper />)}
                    </LocalizationContextProvider>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.hero-image-root')
        );
    });

    hydrateContent(store, Template);

    import(
        /* webpackChunkName: "site-footer" */
        '../../shared/components/common/site-footer/AsyncSiteFooter'
    ).then(({AsyncSiteFooter}) => {
        render(
            <ApolloProvider client={client({prefix, siteContext})}>
                <Provider store={store}>
                    <LazyLoad offset={LAZY_LOAD_OFFSET_DEFAULT}>
                        {withErrorBoundary(<AsyncSiteFooter />)}
                    </LazyLoad>
                </Provider>
            </ApolloProvider>,
            document.querySelector('.site-footer-root')
        );
    });
}
